import React, { useContext, useState, useMemo, useEffect } from 'react';
import {
    Dialog, DialogContent, DialogActions, Button, LinearProgress, Box, Typography, Divider, Stack, Tooltip
} from '@mui/material';
import { UserContext } from '../contexts/UserContext';
import LevelUpDialog from './LevelUpDialog';
import EvolveDialog from './EvolveDialog';
import AscendDialog from './AscendDialog';
import { getXpForLevel, getCardStats, getMaxLevel, getAscensionCost } from '../utilities';
import { PlayingCard, rarityColor } from './PlayingCard';

const GOLD = '#d4af37';
const GOLD_SOFT = 'rgba(212,175,55,0.75)';
const GOLD_DIM = 'rgba(212,175,55,0.6)';
const TEXT_WARM = '#f9f6e3';
const BLACK_DEEP = '#0a0a0a';

function CardInfoDialog(props) {
    const {
        open,
        onClose,
        card,
        onAddToDeck,
        onRemoveFromDeck,
        onAddToDefenseDeck,
        onRemoveFromDefenseDeck,
        readOnly = false,

        // Optional: allow consumers (like TradeOffers) to show a single CTA button
        primaryActionLabel,
        onPrimaryAction,
        primaryActionDisabled = false,
        hideDefaultActions = false,
        hideCloseButton = false,

        // Optional: allow consumers (like Bazaar) to inject custom footer controls (input + button)
        footerContent = null,
    } = props;

    const { userData, levelUpCard, globalCards, evolveCard, ascendCard } = useContext(UserContext);

    const [levelDialogOpen, setLevelDialogOpen] = useState(false);
    const [evolveDialogOpen, setEvolveDialogOpen] = useState(false);
    const [ascendDialogOpen, setAscendDialogOpen] = useState(false);

    const live = useMemo(() => {
        if (!card) return { liveCard: null, missing: true };
        if (!card.instanceId) return { liveCard: card, missing: false };
        const fresh = userData?.ownedCards?.find(c => c.instanceId === card.instanceId);
        return fresh ? { liveCard: { ...card, ...fresh }, missing: false } : { liveCard: card, missing: true };
    }, [userData?.ownedCards, card]);

    const liveCard = live.liveCard;
    const missing = live.missing;

    useEffect(() => {
        if (readOnly) return;
        if (open && card?.instanceId && missing) {
            const t = setTimeout(() => onClose?.(), 0);
            return () => clearTimeout(t);
        }
    }, [open, missing, card?.instanceId, onClose, readOnly]);

    const base = useMemo(
        () => (liveCard ? (globalCards?.[liveCard.cardId] || {}) : {}),
        [globalCards, liveCard?.cardId, liveCard]
    );

    const evoStage = liveCard?.evolutionStage ?? 1;
    const evo = useMemo(
        () => (liveCard ? (base?.evolutions?.[evoStage] || {}) : {}),
        [base?.evolutions, evoStage, liveCard]
    );

    const displayCard = useMemo(() => {
        if (!liveCard) return null;
        const { attack, defense } = getCardStats(liveCard, base || {});
        return { ...liveCard, attack, defense };
    }, [liveCard?.instanceId, liveCard?.level, liveCard?.evolutionStage, liveCard?.cardId, base]);

    // ======== Description + Ability meta (with evo fallback) ========
    const abilityMeta = useMemo(() => {
        if (!liveCard) {
            return {
                description: '',
                abilityDescription: '',
                ability: [],
            };
        }

        const description = base?.description || '';

        let resolvedAbility = [];
        const evoAbility = evo?.ability;
        const baseAbility = base?.ability;

        if (Array.isArray(evoAbility) && evoAbility.length > 0) {
            resolvedAbility = evoAbility;
        } else if (Array.isArray(baseAbility) && baseAbility.length > 0) {
            resolvedAbility = baseAbility;
        } else {
            resolvedAbility = [];
        }

        const resolvedAbilityDescription =
            (evo && typeof evo.abilityDescription === 'string' && evo.abilityDescription.trim().length > 0)
                ? evo.abilityDescription
                : (typeof base?.abilityDescription === 'string' ? base.abilityDescription : '');

        return {
            description,
            abilityDescription: resolvedAbilityDescription,
            ability: resolvedAbility,
        };
    }, [liveCard, base, evo]);

    const { description, abilityDescription, ability } = abilityMeta;

    const abilityLines = useMemo(() => {
        if (!Array.isArray(ability) || ability.length === 0) {
            return abilityDescription ? [abilityDescription] : [];
        }

        const lines = ability
            .map((effect) => {
                if (!effect) return null;

                const rawTarget = String(effect.target || '');
                const segments = rawTarget
                    .split('.')
                    .map(s => s.trim())
                    .filter(Boolean);

                if (!segments.length) return null;

                const className = segments[0];

                let statName = 'stat';
                let inferredSide = null;

                segments.forEach((seg) => {
                    const low = seg.toLowerCase();
                    if (low === 'attack' || low === 'atk') statName = 'attack';
                    else if (low === 'defense' || low === 'def') statName = 'defense';
                    else if (low === 'enemy' || low === 'opponent') inferredSide = 'enemy';
                    else if (low === 'self' || low === 'ally' || low === 'friendly') inferredSide = 'self';
                });

                let sideKey = 'all';
                const tt = (effect.targetType || '').toLowerCase();

                if (tt === 'self' || tt === 'ally' || tt === 'friendly') sideKey = 'self';
                else if (tt === 'enemy' || tt === 'opponent') sideKey = 'enemy';
                else if (inferredSide) sideKey = inferredSide;

                let sidePhrase = 'all';
                if (sideKey === 'self') sidePhrase = 'your';
                else if (sideKey === 'enemy') sidePhrase = "your opponent's";

                const classPhrase = `${sidePhrase === 'all' ? 'all' : sidePhrase} ${className} cards' ${statName}`;

                const flat = Number(effect.flatAmount || 0);
                const pct = Number(effect.percent || 0);

                if (!flat && !pct) return null;

                let amountPart = '';
                if (pct && flat) amountPart = `${pct}% and ${flat}`;
                else if (pct) amountPart = `${pct}%`;
                else amountPart = `${flat}`;

                const isBuff = effect.increase && !effect.decrease;
                const isDebuff = effect.decrease && !effect.increase;

                if (isBuff) return `Boosts ${classPhrase} by ${amountPart}.`;
                if (isDebuff) return `Reduces ${classPhrase} by ${amountPart}.`;

                return `Changes ${classPhrase} by ${amountPart}.`;
            })
            .filter(Boolean);

        if (lines.length === 0 && abilityDescription) return [abilityDescription];
        return lines;
    }, [ability, abilityDescription]);

    // ======== Ascension math ========
    const rarity = base?.rarity || 'Common';
    const baseCap = useMemo(() => getMaxLevel(rarity), [rarity]);
    const ascensions = Number(liveCard?.ascensions || 0);
    const currentCap = baseCap + ascensions * 10;

    const userSoulDust = Number(userData?.consumables?.soulDust ?? 0);
    const nextAscendCost = useMemo(
        () => (rarity ? getAscensionCost(rarity, ascensions) : 0),
        [rarity, ascensions]
    );

    const atOrAboveCap = Number(liveCard?.level || 1) >= currentCap;
    const maxEvo = Number(liveCard?.evolutionStage || 0) === 3;
    const canShowAscend = atOrAboveCap && maxEvo;
    const canAffordAscend = userSoulDust >= nextAscendCost;

    const handleAscendConfirmed = async () => {
        if (!liveCard?.instanceId) return;
        await ascendCard(liveCard.instanceId, { cost: nextAscendCost });
        setAscendDialogOpen(false);
    };
    // ================================

    const xpToNextLevel = useMemo(
        () => Math.max(1, getXpForLevel(liveCard?.level || 1)),
        [liveCard?.level]
    );

    const xpPercent = useMemo(() => {
        if (!liveCard) return 0;
        return Math.min(((liveCard.xp || 0) / xpToNextLevel) * 100, 100);
    }, [liveCard?.xp, xpToNextLevel]);

    const isInstanceCard = !!liveCard?.instanceId;
    const rColor = rarityColor(liveCard?.rarity || 'Common');

    const isInActiveDeck = !!liveCard?.active;
    const isInDefenseDeck = !!liveCard?.defenseActive;

    const activeDeckCount = useMemo(
        () => userData?.ownedCards?.filter(c => c.active).length || 0,
        [userData?.ownedCards]
    );
    const defenseDeckCount = useMemo(
        () => userData?.ownedCards?.filter(c => c.defenseActive).length || 0,
        [userData?.ownedCards]
    );

    const activeDeckFull = activeDeckCount >= 5;
    const defenseDeckFull = defenseDeckCount >= 5;

    const handleLevelUp = (sacrificedIds) => {
        if (!liveCard?.instanceId) return;
        levelUpCard(liveCard.instanceId, sacrificedIds);
    };

    const handleEvolve = (sacrificeInstanceId) => {
        if (!liveCard?.instanceId) return;
        evolveCard(liveCard.instanceId, sacrificeInstanceId);
    };

    const showDefaultActionCluster =
        isInstanceCard && !readOnly && displayCard && !hideDefaultActions;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    background: `linear-gradient(180deg, rgba(0,0,0,0.85) 0%, ${BLACK_DEEP} 100%)`,
                    border: `1px solid ${GOLD_DIM}`,
                    boxShadow: `0 24px 64px rgba(0,0,0,0.7), 0 0 24px ${rColor}33`,
                    color: TEXT_WARM,
                    borderRadius: 2,
                    overflow: 'hidden',
                },
            }}
        >
            <DialogContent
                sx={{
                    pt: 2,
                    background: `
                        radial-gradient(1200px 500px at 50% 120%, rgba(212,175,55,0.06), transparent 60%),
                        radial-gradient(800px 400px at -10% -20%, rgba(212,175,55,0.04), transparent 60%)
                    `,
                }}
            >
                {!displayCard ? (
                    <Box sx={{ py: 4, textAlign: 'center', color: GOLD_DIM }}>
                        <Typography variant="body2">Loading card…</Typography>
                    </Box>
                ) : (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, mt: 4 }}>
                            <PlayingCard showStats showLevel card={displayCard} size="lg" wrapName />
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                <Typography variant="caption" sx={{ color: GOLD_DIM, fontFamily: `'Cinzel', serif` }}>
                                    Level {liveCard.level} / {currentCap}
                                </Typography>
                                <Typography variant="caption" sx={{ color: GOLD_DIM, fontFamily: `'Cinzel', serif` }}>
                                    {liveCard.xp} / {xpToNextLevel} XP
                                </Typography>
                            </Stack>
                            <LinearProgress
                                variant="determinate"
                                value={xpPercent}
                                sx={{
                                    height: 10,
                                    borderRadius: 10,
                                    backgroundColor: 'rgba(255,255,255,0.08)',
                                    '& .MuiLinearProgress-bar': {
                                        background: `linear-gradient(90deg, ${GOLD} 0%, ${rColor} 100%)`,
                                        boxShadow: `0 0 12px ${GOLD_SOFT}`,
                                    },
                                }}
                            />
                            <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: GOLD_DIM }}>
                                Ascensions: {ascensions}
                            </Typography>
                        </Box>

                        {(description || abilityLines.length > 0) && (
                            <Box
                                sx={{
                                    mt: 2,
                                    px: 2,
                                    py: 1.5,
                                    borderRadius: 2,
                                    border: `1px solid ${GOLD_DIM}`,
                                    background: `linear-gradient(180deg, rgba(0,0,0,0.8) 0%, ${BLACK_DEEP} 100%)`,
                                    color: TEXT_WARM,
                                    boxShadow: `inset 0 0 10px rgba(0,0,0,0.7)`,
                                    fontFamily: `'Cinzel', serif`,
                                    maxHeight: 200,
                                    overflowY: 'auto',
                                }}
                            >
                                {description && (
                                    <Typography variant="body2" sx={{ lineHeight: 1.4, mb: abilityLines.length ? 1 : 0 }}>
                                        {description}
                                    </Typography>
                                )}

                                {abilityLines.length > 0 && (
                                    <>
                                        {description && (
                                            <Divider sx={{ my: 1, borderColor: GOLD_DIM, opacity: 0.6 }} />
                                        )}
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, letterSpacing: 0.5 }}>
                                            Abilities
                                        </Typography>
                                        <Box component="ul" sx={{ m: 0, pl: 2, listStyle: 'disc', typography: 'body2', lineHeight: 1.4 }}>
                                            {abilityLines.map((line, idx) => (
                                                <Box component="li" key={idx} sx={{ mb: 0.25 }}>
                                                    {line}
                                                </Box>
                                            ))}
                                        </Box>
                                    </>
                                )}
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    borderTop: `1px solid ${GOLD_DIM}`,
                    background: `linear-gradient(0deg, rgba(0,0,0,0.6), rgba(0,0,0,0.3))`,
                    px: 2,
                    py: 1.5,
                }}
            >
                {/* Optional injected footer UI (e.g., Bazaar price input + list button) */}
                {footerContent ? (
                    <Box sx={{ width: '100%' }}>
                        {footerContent}
                    </Box>
                ) : (
                    <>
                        {/* Optional single-CTA mode (TradeOffers uses this) */}
                        {primaryActionLabel && onPrimaryAction && (
                            <Button
                                onClick={onPrimaryAction}
                                disabled={!!primaryActionDisabled}
                                sx={{
                                    mr: 'auto',
                                    px: 2,
                                    py: 0.75,
                                    textTransform: 'none',
                                    fontWeight: 800,
                                    letterSpacing: 0.6,
                                    color: '#000',
                                    background: `linear-gradient(180deg, ${GOLD} 0%, #c49b2a 100%)`,
                                    border: `1px solid ${GOLD_DIM}`,
                                    boxShadow: `0 0 14px ${GOLD_SOFT}, 0 6px 18px rgba(0,0,0,0.45)`,
                                    borderRadius: 2,
                                    '&:hover': {
                                        background: `linear-gradient(180deg, #e2c35b 0%, ${GOLD} 100%)`,
                                        boxShadow: `0 0 18px ${GOLD_SOFT}, 0 8px 22px rgba(0,0,0,0.55)`,
                                    },
                                    '&:disabled': {
                                        opacity: 0.55,
                                        color: '#2b2b2b',
                                        background: 'linear-gradient(180deg, #5f5630, #4b421d)',
                                        boxShadow: 'none',
                                    },
                                }}
                            >
                                {primaryActionLabel}
                            </Button>
                        )}

                        {showDefaultActionCluster && (
                            <Stack direction="row" spacing={1} sx={{ mr: 'auto' }}>
                                {liveCard.level < currentCap && (
                                    <Button
                                        onClick={() => setLevelDialogOpen(true)}
                                        sx={{
                                            px: 2, py: 0.75, textTransform: 'none', fontWeight: 700, letterSpacing: 0.5,
                                            color: '#000',
                                            background: `linear-gradient(180deg, ${GOLD} 0%, #c49b2a 100%)`,
                                            border: `1px solid ${GOLD_DIM}`,
                                            boxShadow: `0 4px 14px rgba(212,175,55,0.3)`,
                                            borderRadius: 2,
                                            '&:hover': {
                                                background: `linear-gradient(180deg, #e2c35b 0%, ${GOLD} 100%)`,
                                                boxShadow: `0 6px 18px rgba(212,175,55,0.45)`,
                                            },
                                        }}
                                    >
                                        Level Up
                                    </Button>
                                )}

                                {liveCard.evolutionStage < 3 && liveCard.rarity !== 'Common' && (
                                    <Button
                                        onClick={() => setEvolveDialogOpen(true)}
                                        sx={{
                                            px: 2, py: 0.75, textTransform: 'none', fontWeight: 700, letterSpacing: 0.5,
                                            color: TEXT_WARM,
                                            border: `1px solid ${GOLD_DIM}`,
                                            background: `linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.7) 100%)`,
                                            boxShadow: `0 4px 14px rgba(0,0,0,0.35), 0 0 10px ${GOLD_SOFT}`,
                                            borderRadius: 2,
                                            '&:hover': { borderColor: GOLD },
                                        }}
                                    >
                                        Evolve
                                    </Button>
                                )}

                                {canShowAscend && (
                                    <Tooltip
                                        title={
                                            canAffordAscend
                                                ? `Spend ${nextAscendCost} Soul Dust to Ascend (+10 levels)`
                                                : `Need ${nextAscendCost} SD (You have ${userSoulDust})`
                                        }
                                    >
                                        <span>
                                            <Button
                                                onClick={() => setAscendDialogOpen(true)}
                                                disabled={!canAffordAscend}
                                                sx={{
                                                    px: 2, py: 0.75, textTransform: 'none', fontWeight: 800, letterSpacing: 0.6,
                                                    color: '#000',
                                                    background: `linear-gradient(180deg, ${GOLD} 0%, #c49b2a 100%)`,
                                                    border: `1px solid ${GOLD_DIM}`,
                                                    boxShadow: `0 0 14px ${GOLD_SOFT}, 0 6px 18px rgba(0,0,0,0.45)`,
                                                    borderRadius: 2,
                                                    '&:hover': {
                                                        background: `linear-gradient(180deg, #e2c35b 0%, ${GOLD} 100%)`,
                                                        boxShadow: `0 0 18px ${GOLD_SOFT}, 0 8px 22px rgba(0,0,0,0.55)`,
                                                    },
                                                }}
                                            >
                                                Ascend (+10)
                                                <Box component="span" sx={{ ml: 1.25, fontWeight: 700, opacity: 0.85 }}>
                                                    • {nextAscendCost} SD
                                                </Box>
                                            </Button>
                                        </span>
                                    </Tooltip>
                                )}

                                {!isInActiveDeck && onAddToDeck && !activeDeckFull && (
                                    <Button
                                        onClick={() => onAddToDeck?.()}
                                        sx={{
                                            px: 2, py: 0.75,
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            letterSpacing: 0.5,
                                            color: '#000',
                                            background: `linear-gradient(180deg, ${GOLD} 0%, #c49b2a 100%)`,
                                            border: `1px solid ${GOLD_DIM}`,
                                            boxShadow: `0 4px 14px rgba(212,175,55,0.3)`,
                                            borderRadius: 2,
                                            '&:hover': {
                                                background: `linear-gradient(180deg, #e2c35b 0%, ${GOLD} 100%)`,
                                                boxShadow: `0 6px 18px rgba(212,175,55,0.45)`,
                                            },
                                        }}
                                    >
                                        Add to Active Deck
                                    </Button>
                                )}
                                {isInActiveDeck && onRemoveFromDeck && (
                                    <Button
                                        onClick={() => onRemoveFromDeck?.()}
                                        sx={{
                                            px: 2, py: 0.75,
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            letterSpacing: 0.5,
                                            color: TEXT_WARM,
                                            border: `1px solid ${GOLD_DIM}`,
                                            background: `linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.8) 100%)`,
                                            borderRadius: 2,
                                            '&:hover': { borderColor: GOLD },
                                        }}
                                    >
                                        Remove from Active
                                    </Button>
                                )}

                                {!isInDefenseDeck && onAddToDefenseDeck && !defenseDeckFull && (
                                    <Button
                                        onClick={() => onAddToDefenseDeck?.()}
                                        sx={{
                                            px: 2, py: 0.75,
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            letterSpacing: 0.5,
                                            color: '#000',
                                            background: `linear-gradient(180deg, ${GOLD} 0%, #c49b2a 100%)`,
                                            border: `1px solid ${GOLD_DIM}`,
                                            boxShadow: `0 4px 14px rgba(212,175,55,0.3)`,
                                            borderRadius: 2,
                                            '&:hover': {
                                                background: `linear-gradient(180deg, #e2c35b 0%, ${GOLD} 100%)`,
                                                boxShadow: `0 6px 18px rgba(212,175,55,0.45)`,
                                            },
                                        }}
                                    >
                                        Add to Defense Deck
                                    </Button>
                                )}
                                {isInDefenseDeck && onRemoveFromDefenseDeck && (
                                    <Button
                                        onClick={() => onRemoveFromDefenseDeck?.()}
                                        sx={{
                                            px: 2, py: 0.75,
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            letterSpacing: 0.5,
                                            color: TEXT_WARM,
                                            border: `1px solid ${GOLD_DIM}`,
                                            background: `linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.8) 100%)`,
                                            borderRadius: 2,
                                            '&:hover': { borderColor: GOLD },
                                        }}
                                    >
                                        Remove from Defense
                                    </Button>
                                )}
                            </Stack>
                        )}

                        {!hideCloseButton && (
                            <Button
                                onClick={onClose}
                                sx={{
                                    px: 2, py: 0.75, textTransform: 'none', fontWeight: 700, letterSpacing: 0.5,
                                    color: TEXT_WARM,
                                    border: `1px solid ${GOLD_DIM}`,
                                    background: `linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.7) 100%)`,
                                    borderRadius: 2,
                                    '&:hover': { borderColor: GOLD },
                                }}
                            >
                                Close
                            </Button>
                        )}
                    </>
                )}
            </DialogActions>

            {/* Sub-dialogs (unchanged) */}
            <LevelUpDialog
                open={levelDialogOpen}
                onClose={() => setLevelDialogOpen(false)}
                card={liveCard || {}}
                userCards={userData?.ownedCards.map((instance) => {
                    const b = globalCards[instance.cardId];
                    return { ...b, ...instance };
                })}
                onConfirm={handleLevelUp}
            />

            <EvolveDialog
                open={evolveDialogOpen}
                onClose={() => setEvolveDialogOpen(false)}
                card={liveCard || {}}
                userCards={userData?.ownedCards.map((instance) => {
                    const b = globalCards[instance.cardId];
                    return { ...b, ...instance };
                })}
                onConfirm={handleEvolve}
            />

            <AscendDialog
                open={ascendDialogOpen}
                onClose={() => setAscendDialogOpen(false)}
                onConfirm={handleAscendConfirmed}
                cost={nextAscendCost}
                userSoulDust={userSoulDust}
                cardName={base?.name || liveCard?.cardId || 'Card'}
                rarity={rarity}
                currentAscensions={ascensions}
            />
        </Dialog>
    );
}

export default CardInfoDialog;
